import { Component, OnInit, ViewChild } from '@angular/core';
import { catchError, debounceTime } from 'rxjs/operators';
import { Observable, Subscription, Subject, throwError } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { HttpClient } from '@angular/common/http';
import Swal from 'sweetalert2';

import { ACTIONS as SERVICE_ACTIONS } from '@teem/constants/service';
import { StoreServiceService } from '@teem/services/store-service.service';
import { LoaderService } from '@teem/services/loader.service';
import { SharedDataService } from '@teem/services/shared-data.service';

import { services as ServicesApi } from '@teem/constants/api';
import { environment } from '@teem/environments/environment';
import { messages as i18n } from '@teem/constants/messages';
import { queryString } from '@teem/helpers/functions';
import { ImportServiceModalComponent } from '@teem/service-module/modals/import-service-modal/import-service-modal.component';
import { AddServicesComponent } from '@teem/service-module/modals/add-services/add-services.component';

@Component({
  selector: 'app-store-services',
  templateUrl: './store-services.component.html',
  styleUrls: ['./store-services.component.scss']
})
export class StoreServicesComponent implements OnInit {

  @ViewChild('searchInput') searchInput: any;
  categories: any[] = [];
  allServiceCount: number = 0;
  services: any[] = [];
  isSearch: boolean = false;
  loading: boolean = false;
  search: Subject<string> = new Subject<string>();
  searchText: string;
  serverUrl: string = ServicesApi.endPoint;
  category: any = null;

  searchSubscription: Subscription;
  paginator: any = {
    id: 'service-table-paginator',
    currentPage: 1,
    itemsPerPage: environment.pageSize,
    totalItems: 0
  }
  hasStore:boolean = false;
  initialized: boolean = false;
  
  isServicesSelected:boolean = false;
  selectedServices: any[] = [];

  constructor(
    private storeServiceService: StoreServiceService,
    private loaderService: LoaderService,
    private httpClient: HttpClient,
    private modalService: NgbModal,
    private sharedDataService: SharedDataService
  ) { }

  ngOnInit(): void {
    this.searchSubscription = this.search.asObservable().pipe(debounceTime(300)).subscribe(
      (data) => {
        if (this.searchText != data) {
          this.searchText = data;
          if (this.searchText.length == 0 || this.searchText.length >= 3) {
            this.getPage(1, false);
          }
        }
      }
    );

    this.sharedDataService.asObservable().pipe(debounceTime(100)).subscribe((data) => {
      if(data && data.userInfo) {
        this.hasStore = data.userInfo.has_store

        if(this.hasStore) {
          this.categoryList();
          this.getPage(1);
        } else {
          this.initialized = true;
        }
      }
    });
  }

  categoryList() {
    this.loaderService.startLoading();
    this.storeServiceService.categories().subscribe(
      (res: any) => {
        this.loaderService.stopLoading();
        // this.categories = res.categories;
        this.categories = res.categories.filter((item) => item.services_count > 0);
        this.allServiceCount = res.all_service_count;
      },
      err => {
        this.loaderService.stopLoading();
      }
    );
  }

  searchServices(term: string) {
    this.search.next(term);
  }

  toggleSearch() {
    this.isSearch = !this.isSearch;
    if (this.isSearch) {
      this.searchInput.nativeElement.focus();
    } else {
      this.searchInput.nativeElement.value = "";
      this.searchServices("");
    }
  }

  filterByCategory(category=null) {
    this.category = category ? category.id : null;
    this.getPage(1, true);
  }

  isActive(value=null) {
    return this.category === value;
  };


  public getPage(page: number, loading: boolean = true, additionalParams: any = null) {
    this.isServicesSelected = false;
    // Unselect selected items
    if (loading) {
      this.loaderService.startLoading();
      this.loading = true; // to show loading text in table
    }

    let params: any = {
      page: page,
      limit: this.paginator.itemsPerPage
    }

    if (this.searchText && this.searchText.length >=3) {
      params.q = this.searchText;
    }

    if (this.category) {
      params.category = this.category
    }

    if (additionalParams) {
      Object.assign(params, additionalParams);
    }

    this.getData(params).subscribe(
      (r) => {
        this.initialized = true;

        if (r.meta) {
          this.paginator.totalItems = r.meta.total;
          this.paginator.currentPage = r.meta.current_page;
          this.services = r.data;
        }

        this.loaderService.stopLoading();
        this.loading = false;
      }, (err) => {
        this.initialized = true;
        this.loading = false;
        this.loaderService.stopLoading()
      }
    )
  }

  getData(params): Observable<any> {
    let url = this.serverUrl;

    if (url.indexOf('?') == -1)
      url += ('?' + queryString(params));
    else
      url += ('&' + queryString(params));

    return this.httpClient.get(`${url}`).pipe(
      catchError((err: any) => {
        this.loaderService.stopLoading()
        return throwError(err);
      })
    )
  }

  ngOnDestroy() {
    this.searchSubscription.unsubscribe();
  }

  openAddServiceModal() {
    const modalRef = this.modalService.open(AddServicesComponent);
    const parentElement = modalRef.componentInstance.el.nativeElement.parentNode.parentNode.parentElement;

    // Set id on modal dialog
    parentElement.id = "add-service";
    parentElement.classList.add( "store");
    // parentElement.classList.add( "add-services-modal");
    // this.renderer.addClass(parentElement, 'new-product-service show');

    modalRef.result.then(result => {
      this.categoryList();
      this.getPage(1);
    }, fallbackResult => {
      // do nothing
    })
  }

  openImportModal() {
    const modalRef = this.modalService.open(ImportServiceModalComponent);
    // Set id on modal dialog
    modalRef.componentInstance.el.nativeElement.parentNode.parentNode.parentNode.id = "newClient";
    modalRef.result.then(result => {
      this.categoryList();
      this.getPage(1);
    }, fallbackResult => {
      // do nothing
    })
  }

  onEditService() {
    this.categoryList();
    this.getPage(1, false);
  }

  onSelectProduct(selected: Array<any>) {
    this.isServicesSelected = selected.length > 0;
    this.selectedServices = selected;
  }
  
  onDeleteService(data:any) {
    if (data.uuid) {
      Swal.fire({
        title: i18n.are_you_sure,
        text: i18n.service.you_want_to_delete_service,
        icon: 'info',
        showCloseButton: true,
        showCancelButton: true,
        focusConfirm: false,
        confirmButtonText: i18n.yes,
        cancelButtonText: i18n.no_cancel_it
      }).then( result => {
        if(result.value){
          this.loaderService.startLoading();
          this.storeServiceService.action({'do': SERVICE_ACTIONS.DELETE, 'ids': [data.uuid]}).subscribe(data => {
            this.loaderService.stopLoading();
            Swal.fire(i18n.deleted, i18n.service.deleted, 'success');
            this.categoryList();
            this.getPage(1);
          }, err => {
            this.loaderService.stopLoading();
            Swal.fire(i18n.alert.error, i18n.getError(err), 'error');
          });
        }
      });
    }
  }

  deleteServices() {
    if (this.selectedServices.length) {
      Swal.fire({
        title: i18n.are_you_sure,
        text: i18n.service.you_want_to_delete_service,
        icon: 'info',
        showCloseButton: true,
        showCancelButton: true,
        focusConfirm: false,
        confirmButtonText: i18n.yes,
        cancelButtonText: i18n.no_cancel_it
      }).then( result => {
        if(result.value){
          this.loaderService.startLoading();
          this.storeServiceService.action({'do': SERVICE_ACTIONS.DELETE, 'ids': this.selectedServices}).subscribe(data => {
            this.loaderService.stopLoading();
            Swal.fire(i18n.deleted, i18n.service.deleted, 'success');
            this.categoryList();
            this.getPage(1);
          }, err => {
            this.loaderService.stopLoading();
            Swal.fire(i18n.alert.error, i18n.getError(err), 'error');
          });
        }
      });
    }
  }

  exportXls() {
    (window as any).open(`${environment.baseUrl}/services/download`, '_self', 'location=yes');
  }

}
